import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Modal from '../../../components/ui/Modal';
import AppLoadingOverlay from '../../../components/ui/AppLoadingOverlay';
import ClientDocumentIntakeReview from '../../../components/clients/ClientDocumentIntakeReview';
import { prepareIntakeFiles, validateIntakeFiles, categoryLabels } from '../../../utils/clientDocumentIntake';
import { publicIntakeRequest, publicIntakePlan, resolvePublicIntake } from '../../../utils/publicKycIntake';
import { applyKycIntake } from '../../kycDocumentIntake';
import { companyReadingSummary, companyReadings, documentCountryNote, matchedDocumentLabel } from '../../../utils/companyDocumentReading';
import { applyCompanyReading } from '../../../utils/companyReadingPrefill';
import KycCompanyReadingReview from './KycCompanyReadingReview';
import KycEntityTypeChoice from './KycEntityTypeChoice';
import { applyClientEntityType, clientEntityTypeOf, entityTypeComplete, entityTypeLabel, typeChangeClearsDetails, UPLOAD_FIRST_ENTITY_TYPES } from '../../clientEntityType';
import { appConfirm } from '../../../components/ui/appConfirm';
import KycEntityDocumentsGuide, { entityDocumentsGuide } from './KycEntityDocumentsGuide';
import fileUploadIcon from '../../../images/file-upload.svg';
import { useLockBodyScroll } from '../common/common_utils/useLockBodyScroll';
import '../css/KycInitialDocumentsModal.css';

// registrationCount: company documents to provide together (1 to 3), drawn as a stack of sheets.
function DocumentsIllustration({ later = false, registration = false, registrationCount = 1, showId = true, showAddress = true, showCv = false }) {
  const sheets = Math.max(1, Math.min(3, registrationCount));
  // The front sheet moves down-left so the sheets behind it (up-right) stay inside the drawing.
  const front = { x: -6 * (sheets - 1), y: 6 * (sheets - 1) };
  return <svg className="kycInitialDocsIllustration" data-sheets={registration ? sheets : undefined} viewBox="0 0 200 126" fill="none" aria-hidden="true">
    {registration ? <>
      {/* On hover the sheets fan out: the front one tilts left, the ones behind tilt right. */}
      {Array.from({ length: sheets - 1 }, (_, i) => sheets - 1 - i).map(k => <g key={k} data-document="registration-sheet" data-sheet={k} className="kycInitialDocsDocumentTile">
        <rect x={58 + front.x + 12 * k} y={8 + front.y - 6 * k} width="80" height="104" rx="11" fill="white" stroke="currentColor" strokeWidth="2" strokeOpacity={k === 2 ? .45 : .7} />
      </g>)}
      <g transform={sheets > 1 ? `translate(${front.x} ${front.y})` : undefined}><g data-document="registration" className="kycInitialDocsDocumentTile">
      <rect x="58" y="8" width="80" height="104" rx="11" fill="white" stroke="currentColor" strokeWidth="2" />
      <path d="M74 28h48M74 40h36" stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity=".45" />
      <path d="M74 56h48M74 66h48M74 76h30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity=".35" />
      <circle cx="112" cy="94" r="10" fill="#e8eff4" stroke="currentColor" strokeWidth="1.8" />
      <path d="m107.5 94 3 3 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </g></g>
      <circle cx="166" cy="101" r="18" fill="#52768e" />
      <path d="M166 109V93m-6 6 6-6 6 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </> : later ? <>
      <g className="kycInitialDocsDocumentTile kycInitialDocsLaterDocument">
      <rect x="48" y="10" width="84" height="104" rx="12" fill="white" stroke="currentColor" strokeWidth="2" />
      <rect x="66" y="30" width="36" height="7" rx="3.5" fill="currentColor" opacity=".25" />
      {[52, 70, 88].map(y => <g key={y}><rect x="65" y={y} width="9" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" /><path d={`M83 ${y + 4}h30`} stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity=".35" /></g>)}
      </g>
      <g className="kycInitialDocsDocumentTile kycInitialDocsLaterClock">
      <circle cx="139" cy="85" r="28" fill="#f4f0ea" stroke="currentColor" strokeWidth="2" />
      <path d="M139 69v17l10 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <path d="m159 29 5 5 9-10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity=".55" />
    </> : <>
      {showCv && <g data-document="cv" transform={showId || showAddress ? 'translate(-32 -2)' : undefined}>
        <g className="kycInitialDocsDocumentTile">
        <rect x="65" y="12" width="72" height="94" rx="10" fill="white" stroke="currentColor" strokeWidth="2" />
        <text x="101" y="43" textAnchor="middle" fill="currentColor" fontSize="18" fontWeight="700">CV</text>
        <path d="M80 57h42M80 68h42M80 79h30M80 90h36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity=".5" />
        </g>
      </g>}
      {showAddress && <g data-document="proof_of_address" transform={!showId && !showCv ? 'translate(-30 0)' : undefined}>
      <g className="kycInitialDocsDocumentTile">
      <rect x="91" y="9" width="77" height="100" rx="11" fill="white" stroke="currentColor" strokeWidth="2" />
      <path d="m114 41 15-12 15 12v18h-30V41Z" fill="#e8eff4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M125 59V47h8v12M109 74h39m-39 11h28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity=".6" />
      </g>
      </g>}
      {showId && <g data-document="id_document" transform={!showAddress ? (showCv ? 'translate(32 0)' : 'translate(28 -14)') : undefined}>
      <g className="kycInitialDocsDocumentTile">
      <g transform="rotate(-8 70 78)">
        <rect x="24" y="48" width="104" height="66" rx="10" fill="#eaf1f6" stroke="currentColor" strokeWidth="2" />
        <rect x="34" y="58" width="30" height="42" rx="5" fill="white" />
        <circle cx="49" cy="71" r="7" fill="currentColor" opacity=".6" />
        <path d="M39 93v-4a10 10 0 0 1 20 0v4" fill="currentColor" opacity=".6" />
        <path d="M76 66h34m-34 11h25m-25 15h30" stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity=".45" />
      </g>
      </g>
      </g>}
      <circle cx="166" cy="101" r="18" fill="#52768e" />
      <path d="M166 109V93m-6 6 6-6 6 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </>}
  </svg>;
}

// Keep both presentations available while the inline card is being evaluated.

function InitialDocumentsSurface({ inline, stepLabel, onClose, children, title = 'How would you like to start?' }) {
  const titleId = useId();
  const reduceMotion = useReducedMotion();
  if (!inline) return <Modal open title={title} className="kycInitialDocsModal" onClose={onClose}>{children}</Modal>;
  return <section className="kycInitialDocsModal kycInitialDocsCard" aria-labelledby={titleId}>
    <div className="uiModalHeader"><div>
      {stepLabel && <div className="mobileKycStageEyebrow">{stepLabel}</div>}
      <AnimatePresence mode="wait" initial={false}>
        <motion.h2 key={title} id={titleId} className="uiModalTitle"
          initial={reduceMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.25, ease: 'easeInOut' }}>{title}</motion.h2>
      </AnimatePresence>
    </div></div>
    <div className="uiModalBody">{children}</div>
  </section>;
}

// SHA-256 of the file content; name, size and date when the browser cannot hash.
async function fileContentKey(file) {
  try {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    return `${file.name}|${file.size}|${file.lastModified}`;
  }
}
const duplicateFilesMessage = names => names.length === 1
  ? `"${names[0]}" is already uploaded.`
  : `These files are already uploaded: ${names.map(name => `"${name}"`).join(', ')}.`;

export default function KycInitialDocumentsModal({ clientId, token, form, separateNames, setForm, onComplete, inline = false, stepLabel, mobile = false, includeCv = false, entity = false, corporateDocuments = null, entityDocTypes = ['registration'] }) {
  const reduceMotion = useReducedMotion();
  const [files, setFiles] = useState([]);
  const [result, setResult] = useState([]);
  const filesRef = useRef([]);
  // Content key of each file in filesRef (same order), to refuse the exact same file twice.
  const fileKeysRef = useRef([]);
  const resultRef = useRef([]);
  const [processingIndex, setProcessingIndex] = useState(0);
  const [review, setReview] = useState(null);
  // Company: "Details found in your documents" before the files are saved.
  const [companyReview, setCompanyReview] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [busy, setBusy] = useState('');
  const [processingHeight, setProcessingHeight] = useState(0);
  const dropZone = useRef(null);
  const [error, setError] = useState('');
  // "… is already uploaded": a notice only (not a reading error, no Try again).
  const [duplicateNote, setDuplicateNote] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);
  const picker = useRef(null);
  const controller = useRef(null);
  const live = useRef(true);
  const inFlight = useRef(false);
  const startTitleId = useId();
  useLockBodyScroll(!inline || !!review);
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; controller.current?.abort(); };
  }, []);

  function resetDrag() {
    dragDepth.current = 0;
    setDragActive(false);
  }

  useEffect(() => {
    const reset = () => { dragDepth.current = 0; setDragActive(false); };
    window.addEventListener('dragend', reset);
    window.addEventListener('drop', reset);
    return () => { window.removeEventListener('dragend', reset); window.removeEventListener('drop', reset); };
  }, []);

  const categories = entity ? ['company_document'] : ['id_document', 'proof_of_address', ...(includeCv ? ['cv'] : [])];
  // Entity: the country of registration names the accepted documents; ask for it when not recorded.
  const registrationCountry = String(form?.customer?.nationalityOrIncorporationCountry || '').trim().toUpperCase();
  const needsCountry = entity && !/^[A-Z]{2}$/.test(registrationCountry);
  // Client entity type (same fields as the ownership step); company documents are uploaded first only
  // for operating, holding and domiciliary companies.
  const entityType = clientEntityTypeOf(form?.ownership);
  const entityTypeAnswered = entity && entityTypeComplete(entityType);
  const uploadAllowed = !entity || (entityTypeAnswered && UPLOAD_FIRST_ENTITY_TYPES.includes(entityType.type));
  const laterNote = entityTypeAnswered && !uploadAllowed
    ? `Documents for a ${entityTypeLabel(entityType).toLowerCase()} are requested at a later stage.` : '';
  const uploadBlocked = needsCountry || !uploadAllowed;
  const applyEntityType = choice => {
    setForm(previous => ({ ...previous, ownership: applyClientEntityType(previous.ownership, choice, previous.customer?.name) }));
    // Trust, foundation, quoted company: no documents at this step, straight to the next one (unless files were
    // already uploaded here; then the client continues themselves).
    if (entityTypeComplete(choice) && !UPLOAD_FIRST_ENTITY_TYPES.includes(choice.type) && !filesRef.current.length) skip();
  };
  const changeEntityType = choice => {
    // A different type clears the ownership details that depend on it: ask first.
    if (typeChangeClearsDetails(form?.ownership, choice)) {
      appConfirm({ group: 'appConfirm', header: 'Change the entity type?', acceptLabel: 'Change', rejectLabel: 'Keep',
        message: 'The ownership details already entered will be removed.', accept: () => applyEntityType(choice) });
      return;
    }
    applyEntityType(choice);
  };
  const entityTypeSlot = entity ? <KycEntityTypeChoice value={entityType} onChange={changeEntityType} mobile={mobile} disabled={!!busy} /> : null;
  // Company type chosen in the card (a case name of the country list, or "not_listed"), kept in the record.
  const companyType = String(form?.customer?.companyType || '');
  const setCompanyType = value => setForm(previous => ({ ...previous, customer: { ...previous.customer, companyType: value || '' } }));
  // "registration documents" unless constitutional / ownership documents are also requested.
  const companyDocs = entityDocTypes.some(type => type === 'constitutional' || type === 'ownership') ? 'company documents' : 'registration documents';
  // Desktop company card: document guide beside the drop zone, skip as a bar underneath.
  const entityDesktop = entity && !mobile;
  // A company has one country in the form: the company details section keeps the address country and
  // the country of incorporation identical, so both change together.
  // The company type belongs to the country list, so it is cleared with the country.
  const setRegistrationCountry = value => setForm(previous => ({ ...previous, customer: { ...previous.customer, country: value || '', nationalityOrIncorporationCountry: value || '', companyType: '' } }));
  // A country change on this page reads the uploaded company files again against the new country's list.
  const changeRegistrationCountry = value => {
    setRegistrationCountry(value);
    if (!entity || !value || value === registrationCountry || inFlight.current || !resultRef.current.length) return;
    resultRef.current = []; setResult([]); setResolved(null);
    readFiles({ country: value, companyType: '' });
  };
  const recognized = documents => documents.filter(doc => doc.status === 'extracted' && categories.includes(doc.category));
  const accepted = recognized(result);
  const hasId = accepted.some(doc => doc.category === 'id_document');
  const hasAddress = accepted.some(doc => doc.category === 'proof_of_address');
  const hasCv = accepted.some(doc => doc.category === 'cv');
  const missingCategories = categories.filter(category => !accepted.some(doc => doc.category === category));
  const remaining = (mobile ? categories : missingCategories).map(category => ({ id_document: 'ID', proof_of_address: 'proof of address', cv: 'CV', company_document: 'company document' }[category]));
  // No company type chosen: the types every recognised document belongs to (a hint for the list only).
  const suggestedTypes = (() => {
    if (!entity || companyType) return [];
    // The type the document itself shows (legal form, issuer) when the reader named one that uses the matched
    // document; otherwise every type that uses the matched document.
    const typeLists = result.filter(doc => matchedDocumentLabel(doc)).map(doc => {
      const types = doc.company?.corporate?.companyTypes || [];
      const shown = doc.company?.corporate?.companyType || '';
      return shown && types.includes(shown) ? [shown] : types;
    });
    if (!typeLists.length) return [];
    return typeLists.reduce((common, types) => common.filter(type => types.includes(type)));
  })();
  // Company documents requested together for the chosen company type (the illustration shows that many sheets).
  const registrationCount = (() => {
    if (!entity) return 1;
    const list = corporateDocuments?.country === registrationCountry ? corporateDocuments : null;
    const typeIndex = entityDocumentsGuide(list, null, entityDocTypes).cases.findIndex(kase => kase.name && kase.name === companyType);
    const guide = entityDocumentsGuide(list, typeIndex >= 0 ? typeIndex : null, entityDocTypes);
    return guide.together ? guide.required.length : 1;
  })();
  // Company documents the reader matched to the country list (document name and tick in the guide).
  const tickedDocuments = entity ? [...new Set(result.map(matchedDocumentLabel).filter(Boolean))] : [];
  const requestedDocuments = remaining.length > 1
    ? `${remaining.slice(0, -1).join(', ')} or ${remaining[remaining.length - 1]}` : remaining[0];
  const uploadLabel = entity ? (remaining.length ? `Upload ${companyDocs}` : 'Documents ready')
    : !includeCv && mobile ? 'Upload ID and proof of address'
    : remaining.length ? `Upload ${requestedDocuments}` : 'Documents ready';

  async function addFiles(incoming) {
    if (!live.current || inFlight.current === 'saving' || !incoming?.length || (entity && uploadBlocked)) return;
    // The exact same file (same content) is uploaded once only.
    const candidates = Array.from(incoming);
    const keys = await Promise.all(candidates.map(fileContentKey));
    if (!live.current) return;
    const seen = new Set(fileKeysRef.current);
    const fresh = [], duplicates = [];
    candidates.forEach((file, i) => {
      if (seen.has(keys[i])) { duplicates.push(file.name); return; }
      seen.add(keys[i]); fresh.push({ file, key: keys[i] });
    });
    setDuplicateNote(duplicates.length ? duplicateFilesMessage(duplicates) : '');
    if (!fresh.length) return;
    const nextFiles = [...filesRef.current, ...fresh.map(item => item.file)];
    try { validateIntakeFiles(nextFiles); }
    catch (e) { setError(e.message); return; }
    filesRef.current = nextFiles;
    fileKeysRef.current = [...fileKeysRef.current, ...fresh.map(item => item.key)];
    setFiles(nextFiles); setResolved(null); setError('');
    // The active reader sees new files through the ref, even across rapid drops.
    if (!inFlight.current) readFiles();
  }

  function preserveDropZoneHeight() {
    if (!mobile) setProcessingHeight(dropZone.current?.getBoundingClientRect().height || 0);
  }

  // reading: the country and company type sent with company files (the form state may not be updated yet).
  async function readFiles(reading = { country: registrationCountry, companyType }) {
    if (inFlight.current || !filesRef.current.length || !live.current) return;
    preserveDropZoneHeight();
    inFlight.current = 'reading'; setBusy('Reading your documents…'); setError('');
    const request = new AbortController();
    controller.current = request;
    let completed = false;
    try {
      // Process one document at a time. Drops during an await extend this queue.
      while (live.current && !request.signal.aborted) {
        const index = filesRef.current.findIndex((_, i) => !resultRef.current.some(doc => doc.fileIndex === i));
        if (index < 0) { completed = true; break; }
        setProcessingIndex(index + 1);
        const [file] = await prepareIntakeFiles([filesRef.current[index]]);
        if (!live.current || request.signal.aborted) return;
        const response = await publicIntakeRequest('extract', { clientId, token, files: [file],
          ...(entity ? { country: reading.country, companyType: reading.companyType === 'not_listed' ? '' : reading.companyType } : {}) }, request.signal);
        if (!live.current || request.signal.aborted) return;
        const document = response.documents?.[0];
        if (!document) throw new Error('This document could not be read. Please try again.');
        // Reading errors are not shown to the client (the file keeps its name); kept in the console for support.
        if (document.status === 'error') console.warn('[document intake] Not read:', filesRef.current[index]?.name, document.error?.code || '', document.error?.message || '');
        resultRef.current = [...resultRef.current, { ...document, file, fileIndex: index }];
        setResult(resultRef.current);
      }
    } catch (e) { if (live.current && !request.signal.aborted) setError(e.message); }
    finally { inFlight.current = false; if (live.current) setBusy(''); }
    if (!completed || !live.current || request.signal.aborted) return;
    const readable = recognized(resultRef.current);
    // Do not leave the card until every queued document has finished. A company always confirms with
    // Continue: it may have several documents to upload.
    if (!entity && categories.every(category => readable.some(doc => doc.category === category))) {
      continueWithDocuments(readable);
    }
  }

  // values: individuals, the confirmed fields to pre-fill; companies, the confirmed reading (or null), kept in
  // the record (customer.companyDocumentsReading) and used to fill the empty company, signatory and (desktop)
  // shareholder fields.
  async function finish(values, documents = accepted) {
    if (inFlight.current) return;
    preserveDropZoneHeight();
    inFlight.current = 'saving'; setBusy('Saving your documents…'); setError(''); setReview(null); setCompanyReview(false); setResolved(values);
    controller.current = new AbortController();
    try {
      // A company keeps every uploaded file as a company document, with its AI reading, for staff review.
      const attachments = entity
        ? resultRef.current.map(doc => ({ ...doc.file, category: 'company_document' }))
        : documents.map(doc => ({ ...doc.file, category: doc.category }));
      await publicIntakeRequest('attach', { clientId, token, files: attachments }, controller.current.signal);
      if (!live.current) return;
      if (entity) {
        if (values) setForm(previous => applyCompanyReading(previous, values, { mobile }));
      } else {
        setForm(previous => applyKycIntake(previous, values));
      }
      onComplete();
    } catch (e) { if (live.current) setError(e.message); }
    finally { inFlight.current = false; if (live.current) setBusy(''); }
  }

  function continueWithDocuments(documents = accepted) {
    if (entity) {
      if (companyReadingSummary(resultRef.current).hasData) setCompanyReview(true);
      else finish(null);
      return;
    }
    const plan = publicIntakePlan(documents, form, separateNames, { includeCv });
    if (plan.review.length) setReview(plan);
    else finish(plan.values, documents);
  }

  function removeFile(index) {
    filesRef.current = filesRef.current.filter((_, i) => i !== index);
    fileKeysRef.current = fileKeysRef.current.filter((_, i) => i !== index);
    setDuplicateNote('');
    resultRef.current = resultRef.current.filter(doc => doc.fileIndex !== index).map(doc => ({ ...doc, fileIndex: doc.fileIndex > index ? doc.fileIndex - 1 : doc.fileIndex }));
    setFiles(filesRef.current); setResult(resultRef.current);
    setResolved(null); setError('');
  }

  function skip() {
    if (busy === 'Saving your documents…') return;
    live.current = false;
    controller.current?.abort();
    onComplete();
  }

  if (companyReview) return <KycCompanyReadingReview documents={companyReadings(resultRef.current)} mobile={mobile}
    busy={!!busy} onConfirm={reading => finish(reading)} onSkip={() => finish(null)} onClose={() => setCompanyReview(false)} />;

  if (review) return createPortal(<ClientDocumentIntakeReview review={review} documentsOnly
    onClose={() => setReview(null)} onResolve={(_, choices) => finish(resolvePublicIntake(review, choices))} />, document.body);

  const uploadedFiles = <ul className="kycInitialDocsFiles">{files.map((file, index) => {
          const doc = result?.find(item => item.fileIndex === index);
          if (busy && !doc) return null;
          const readable = doc && recognized([doc]).length > 0;
          // A company's file keeps its name: the reader names documents of the country list later.
          // A company file shows the document it was matched to, otherwise its file name.
          const label = entity ? matchedDocumentLabel(doc) || file.name : readable ? categoryLabels[doc.category] : doc ? 'Unknown document type' : 'Document not read';
          return <li key={`${index}-${file.name}`}>
            <span className={`kycInitialDocsFileIcon${readable || (entity && doc) ? ' is-ready' : ''}`} aria-hidden="true"><i className={readable && !entity ? 'bi bi-check2' : 'bi bi-file-earmark-text'} /></span>
            <span className="kycInitialDocsFileName">{label}{entity && documentCountryNote(doc) && <small className="kycEntityCountryNote"><i className="bi bi-exclamation-triangle" aria-hidden="true" /> {documentCountryNote(doc)}</small>}</span>
            <button type="button" disabled={!!busy} aria-label={`Remove ${label}`} onClick={() => removeFile(index)}><i className="bi bi-x-lg" aria-hidden="true" /></button>
          </li>;
        })}</ul>;

  const dropArea = (
        <div ref={dropZone} style={{ height: !mobile && busy ? processingHeight : undefined }}
          className={`kycInitialDocsDrop${dragActive ? ' is-dragging' : ''}${busy ? ' is-processing' : ''}`} role="group" aria-label="Document upload" aria-busy={!!busy}
          onDragEnter={event => {
            event.preventDefault();
            if (inFlight.current === 'saving' || uploadBlocked || !Array.from(event.dataTransfer.types || []).includes('Files')) return;
            dragDepth.current += 1; setDragActive(true);
          }}
          onDragOver={event => { event.preventDefault(); event.dataTransfer.dropEffect = inFlight.current === 'saving' ? 'none' : 'copy'; }}
          onDragLeave={event => {
            event.preventDefault(); dragDepth.current = Math.max(0, dragDepth.current - 1);
            if (!dragDepth.current) setDragActive(false);
          }}
          onDrop={event => { event.preventDefault(); resetDrag(); if (!uploadBlocked) addFiles(event.dataTransfer.files); }}>
          <svg className="kycInitialDocsDropBorder" aria-hidden="true" focusable="false"><rect x="0.5" y="0.5" rx="17.5" /></svg>
          <button type="button" className="kycInitialDocsOption kycInitialDocsUploadTarget" disabled={!!busy || uploadBlocked}
            aria-label={busy || uploadLabel} aria-busy={!!busy} onClick={() => picker.current?.click()}>
          <span className="kycInitialDocsOptionBadge">{entity ? 'Fill company details automatically' : 'Fill some details automatically'}</span>
          {!mobile && missingCategories.length === 0
            ? <span className="kycInitialDocsReadyIcon" aria-hidden="true"><i className="bi bi-check-circle" /></span>
            : <DocumentsIllustration registration={entity} registrationCount={registrationCount} showId={mobile || !hasId} showAddress={mobile || !hasAddress} showCv={includeCv && (mobile || !hasCv)} />}
          <strong>{entity ? <>Upload {companyDocs.split(' ')[0]}<br />documents</> : includeCv ? uploadLabel : mobile ? <>Upload ID &amp;<br />proof of address</> : !hasId && !hasAddress ? <>Upload ID or<br />proof of address</> : uploadLabel}</strong>
          <span className="kycInitialDocsDropHint" aria-live="polite">
            <span className="kycInitialDocsDesktopHint">{dragActive ? 'Release to add your documents' : <>Drop files here or <span className="kycInitialDocsBrowse">browse</span></>}</span>
            <span className="kycInitialDocsTouchHint">Tap to upload <span aria-hidden="true">→</span></span>
          </span>
          </button>
          {!mobile && files.length > 0 && (!busy || result.length > 0) && (
            <motion.div className="kycInitialDocsUploadedReveal"
              initial={reduceMotion ? false : { height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              transition={{
                height: { duration: reduceMotion ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] },
                opacity: { duration: reduceMotion ? 0 : 0.3, delay: reduceMotion ? 0 : 0.08 },
              }}>
              <div className="kycInitialDocsUploaded" aria-label="Uploaded documents">{uploadedFiles}</div>
            </motion.div>
          )}
          {busy && <div className="kycInitialDocsLoading" role="status" aria-label={busy}>
            <AppLoadingOverlay title={busy} subtitle={busy === 'Reading your documents…' ? `${processingIndex} / ${files.length}` : ''} loaderStyle="dots" bg="transparent" />
            {busy === 'Reading your documents…' && <button type="button" className="kycInitialDocsQueueAdd" onClick={() => picker.current?.click()}>
              {dragActive ? 'Release to add to queue' : mobile ? 'Add more documents' : 'Drop more documents or browse'}
            </button>}
          </div>}
        </div>
  );

  // Mobile company step: own layout and bottom bar (the page hides its Back / Save draft / Continue bar).
  if (entity && mobile) {
    const reading = busy === 'Reading your documents…';
    const saving = busy === 'Saving your documents…';
    const unread = files.some((_, index) => !result.some(doc => doc.fileIndex === index));
    return <section className="kycEntityStartMobile" aria-labelledby={`${startTitleId}-title`}>
      <input ref={picker} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" hidden
        onChange={event => { addFiles(event.target.files); event.target.value = ''; }} />
      <div className="kycEntityStartMobileHead">
        {stepLabel && <div className="mobileKycStageEyebrow">{stepLabel}</div>}
        <h2 id={`${startTitleId}-title`} className="mobileKycStageTitle">Start with your company documents</h2>
        <p className="mobileKycStageHint">These documents are needed for your KYC. Upload them now and we'll fill in some company details for you.</p>
      </div>

      {files.length > 0 && <section className="kycEntityMobileSec kycEntityMobileUploads" aria-label="Your documents">
        <h3 className="kycEntityMobileSecHead"><span className="kycEntityMobileNum is-done"><i className="bi bi-check-lg" aria-hidden="true" /></span>Your documents</h3>
        <ul className="kycEntityMobileFiles">{files.map((file, index) => {
          const doc = result.find(item => item.fileIndex === index);
          return <li key={`${index}-${file.name}`} className={doc ? 'is-ready' : 'is-reading'}>
            <span className="kycEntityMobileFileIcon" aria-hidden="true">
              {doc ? <i className="bi bi-file-earmark-text" /> : <span className="kycEntityGuideSpinner" />}
            </span>
            <span className="kycEntityMobileFileName">{doc ? <>{matchedDocumentLabel(doc) || file.name}{documentCountryNote(doc) && <small className="kycEntityCountryNote"><i className="bi bi-exclamation-triangle" aria-hidden="true" /> {documentCountryNote(doc)}</small>}</> : <>Reading…<small>{file.name}</small></>}</span>
            <button type="button" disabled={!!busy} aria-label={`Remove ${file.name}`} onClick={() => removeFile(index)}>
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </li>;
        })}</ul>
      </section>}

      {error && <p role="alert" className="kycInitialDocsError">{error}</p>}
      {duplicateNote && <p role="status" className="kycInitialDocsDuplicate"><i className="bi bi-exclamation-triangle" aria-hidden="true" /> {duplicateNote}</p>}

      <KycEntityDocumentsGuide mobile country={registrationCountry} corporateDocuments={corporateDocuments}
        onCountryChange={changeRegistrationCountry} disabled={!!busy} docTypes={entityDocTypes}
        companyType={companyType} onCompanyTypeChange={setCompanyType} ticked={tickedDocuments} suggestedTypes={suggestedTypes}
        entityTypeSlot={entityTypeSlot} showDocuments={uploadAllowed} documentsNote={laterNote}
        entityType={entityType} onEntityTypeChange={changeEntityType}
        uploadSlot={!files.length && !laterNote ? <button type="button" className="kycEntityCardUpload"
          disabled={!!busy || uploadBlocked} onClick={() => picker.current?.click()}>
          <span className="kycEntityCardUploadIcon" aria-hidden="true">
            <span className="kycEntityUploadIcon" style={{ WebkitMaskImage: `url(${fileUploadIcon})`, maskImage: `url(${fileUploadIcon})` }} />
          </span>
          Upload document
        </button> : null}
        documentsBadge={!files.length && !busy ? <span className="kycEntityMobileBenefit">
          <i className="bi bi-stars" aria-hidden="true" /><span>Auto-fills <br className="kycEntityBenefitBreak" />some details</span>
        </span> : null} />

      <div className="mobileKycBottomBar kycEntityMobileBar">
        {laterNote ? <button type="button" className="btn mobileKycPrimaryBtn" onClick={skip}>Continue</button>
        : <>
        {/* Secondary action on the left, main action (upload / continue) on the right. */}
        {result.length > 0 || reading
          ? <button type="button" className="btn mobileKycGhostBtn" disabled={!!busy || uploadBlocked} onClick={() => picker.current?.click()}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add document
            </button>
          : <button type="button" className="btn mobileKycGhostBtn kycEntityMobileSkip" aria-label="Skip, provide them later" disabled={saving} onClick={skip}>
              <span>Skip</span><small>Provide them later</small>
            </button>}
        {reading || saving
          ? <button type="button" className="btn mobileKycPrimaryBtn mobileKycPrimaryBtn--blocked" disabled>
              <span className="kycEntityGuideSpinner" aria-hidden="true" />
              {saving ? 'Saving…' : `Reading ${processingIndex} / ${files.length}`}
            </button>
          : error && unread
            ? <button type="button" className="btn mobileKycPrimaryBtn" onClick={() => readFiles()}>Try again</button>
            : result.length > 0
              ? <button type="button" className="btn mobileKycPrimaryBtn" onClick={() => resolved ? finish(resolved) : continueWithDocuments()}>Continue</button>
              : <button type="button" className="btn mobileKycPrimaryBtn" disabled={uploadBlocked} onClick={() => picker.current?.click()}>
                  <span className="kycEntityUploadIcon" aria-hidden="true"
                    style={{ WebkitMaskImage: `url(${fileUploadIcon})`, maskImage: `url(${fileUploadIcon})` }} /> Upload documents
                </button>}</>}
      </div>
    </section>;
  }

  // Desktop company card: the heading follows the stage (entity type first, then the documents).
  const askingEntityType = entityDesktop && !entityTypeAnswered;
  const entityDisplayName = String(form?.customer?.name || '').trim() || 'your organisation';
  const entityTypeQuestion = 'Organisation type';
  const content = <InitialDocumentsSurface inline={inline} stepLabel={stepLabel} onClose={skip}
    title={askingEntityType ? entityTypeQuestion : 'How would you like to start?'}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.p key={askingEntityType ? 'type' : 'documents'} className="kycInitialDocsIntro"
          initial={reduceMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.25, ease: 'easeInOut' }}>{askingEntityType
          ? `Choose the option that best describes ${entityDisplayName}.`
          : entity
          ? `Upload your ${companyDocs === 'company documents' ? 'company documents' : "company's registration documents"} to fill the company details faster, or complete the form first.`
          : 'Upload your documents to fill your details faster, or complete the form first.'}</motion.p>
      </AnimatePresence>
      <input ref={picker} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" hidden
        onChange={event => { addFiles(event.target.files); event.target.value = ''; }} />
      {entityDesktop ? <AnimatePresence mode="wait" initial={false}>
        {!entityTypeAnswered
          // First only the entity type, in the space of the two cards; then the cards fade in.
          ? <motion.div key="entity-type" className="kycInitialDocsEntityType"
              initial={reduceMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.25, ease: 'easeInOut' }}>
              <KycEntityTypeChoice value={entityType} onChange={changeEntityType} variant="cards" disabled={!!busy} />
            </motion.div>
          : <motion.div key="documents" initial={reduceMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.25, ease: 'easeInOut' }}>
        <div className={`kycInitialDocsEntity${laterNote ? ' is-single' : ''}`}>
          <KycEntityDocumentsGuide country={registrationCountry} corporateDocuments={corporateDocuments}
            onCountryChange={changeRegistrationCountry} disabled={!!busy} docTypes={entityDocTypes}
            companyType={companyType} onCompanyTypeChange={setCompanyType} ticked={tickedDocuments} suggestedTypes={suggestedTypes}
            entityTypeSlot={entityTypeSlot} showDocuments={uploadAllowed} documentsNote={laterNote}
            entityType={entityType} onEntityTypeChange={changeEntityType} />
          {!laterNote && dropArea}
        </div>
        {laterNote
          ? <div className="kycInitialDocsSkipBar">
              <span><i className="bi bi-arrow-right-circle" aria-hidden="true" /> Continue to the form.</span>
              <button type="button" onClick={skip}>Continue <span aria-hidden="true">→</span></button>
            </div>
          : <div className="kycInitialDocsSkipBar">
              <span><i className="bi bi-clock" aria-hidden="true" /> Don't have them at hand? Complete the form yourself and upload the documents later.</span>
              <button type="button" aria-label="Skip and upload later" disabled={busy === 'Saving your documents…'} onClick={skip}>
                Skip for now <span aria-hidden="true">→</span>
              </button>
            </div>}
            </motion.div>}
      </AnimatePresence> : <div className="kycInitialDocsChoices">
        {dropArea}
        <span className="kycInitialDocsOr" aria-hidden="true">or</span>
        <button type="button" className="kycInitialDocsOption kycInitialDocsLater" aria-label="Skip and upload later"
          disabled={busy === 'Saving your documents…'} onClick={skip}>
          <span className="kycInitialDocsOptionBadge">Complete the form yourself</span>
          <DocumentsIllustration later />
          <strong>Skip &amp;<br />upload later</strong>
          <span className="kycInitialDocsLaterHint">Continue to form <span aria-hidden="true">→</span></span>
        </button>
      </div>}
      {!entity && <p className="kycInitialDocsNote">{includeCv ? 'Upload any of these documents now, or all three.' : 'You can upload either document now, or both.'}</p>}
      {mobile && files.length > 0 && !busy && <div className="kycInitialDocsSelection">
        <div className="kycInitialDocsSelectionHead"><strong>Uploaded documents</strong></div>
        {uploadedFiles}
      </div>}
      {error && <p role="alert" className="kycInitialDocsError">{error}</p>}
      {duplicateNote && <p role="status" className="kycInitialDocsDuplicate"><i className="bi bi-exclamation-triangle" aria-hidden="true" /> {duplicateNote}</p>}
      {files.length > 0 && !busy && <div className="kycInitialDocsActions">
        {accepted.length === 0 && !entity && <small>You can check and edit the details afterwards.</small>}
        {error && files.some((_, index) => !result.some(doc => doc.fileIndex === index)) && accepted.length > 0 &&
          <button type="button" className="kycInitialDocsContinue kycInitialDocsRetry" onClick={() => readFiles()}>Try again</button>}
        {(entity ? result.length > 0 : accepted.length > 0)
          ? <button type="button" className="kycInitialDocsContinue" disabled={!!busy} onClick={() => resolved ? finish(resolved) : continueWithDocuments()}>Use documents and continue</button>
          : error ? <button type="button" className="kycInitialDocsContinue" onClick={() => readFiles()}>Try again</button> : null}
      </div>}
    </InitialDocumentsSurface>;
  return inline ? content : createPortal(content, document.body);
}
